import React, {useContext, useRef} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import TaskInfo from './TaskInfo';

import {CardType, TaskType} from '../../../constants/dataEnum';

import './TaskInfoWrapper.scss';
import {HighlightTaskContext} from '../ContextProviders/HighlightTaskContext';

const MAX_INFO_LENGTH = 8;

function TaskInfoWrapper({tasks, children, showTaskVolume, fitContent, showTaskNumber, cardType, style}) {
  const {highlightedTask} = useContext(HighlightTaskContext) || {};

  const numberMapRef = useRef({});

  function getTaskVolume(task) {
    switch (task.type) {
      case TaskType.PICK_TIP:
      case TaskType.EJECT_TIP:
        return (task.rowTipCount || 1) * (task.columnTipCount || 1);
      default: {
        let taskVolume = parseFloat(task.volume);
        if (cardType === CardType.POOLING && task.number === 3) {
          taskVolume *= numberMapRef.current[task.number] ? numberMapRef.current[task.number] : 1;
        }

        // eslint-disable-next-line no-restricted-globals
        return !isNaN(taskVolume) ? taskVolume.toFixed(1) : 0;
      }
    }
  }

  const getDistinctTasks = () => {
    const distinctTasks = [];
    const numberMap = {};

    let sortedTasks = [...tasks];
    const key = 'number';

    sortedTasks = sortedTasks.sort((a, b) => {
      if (a[key] > b[key]) {
        return 1;
      }

      if (b[key] > a[key]) {
        return -1;
      }

      return 0;
    });

    sortedTasks.forEach((e) => {
      if (!(e.number in numberMap)) {
        distinctTasks.push(e);

        numberMap[e.number] = 0;
      }
      numberMap[e.number] += 1;
    });

    numberMapRef.current = numberMap;
    return distinctTasks;
  };

  return (
    <div className={`task-info-wrapper ${fitContent && 'fit-content'}`} style={style}>
      <div className="task-info-container">
        {getDistinctTasks().map((task, idx) => {
          const taskVolume = getTaskVolume(task);

          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`task.ID-${idx}`}>
              {task && (idx < MAX_INFO_LENGTH - 1 || null) && (
                <TaskInfo
                  taskNumber={task.sdStep ?? task.number}
                  taskVolume={taskVolume}
                  taskType={task.type}
                  showTaskVolume={
                    (cardType === CardType.BROADCAST && task.number === 2) ||
                    (cardType === CardType.POOLING && task.number === 1)
                      ? false
                      : showTaskVolume
                  }
                  showTaskNumber={showTaskNumber}
                  isHighlighted={
                    cardType !== CardType.SERIAL_DILUTION &&
                    cardType !== CardType.BROADCAST &&
                    cardType !== CardType.POOLING
                      ? highlightedTask?.ID === task.ID
                      : highlightedTask?.ID === task.number
                  }
                  labwareId={task.labwareID}
                />
              )}
              {task && (idx === MAX_INFO_LENGTH - 1 || null) && <TaskInfo taskNumber="..." />}
            </React.Fragment>
          );
        })}
      </div>
      {children}
    </div>
  );
}

TaskInfoWrapper.propTypes = {
  style: PropTypes.object,
  tasks: PropTypes.array,
  children: PropTypes.any,
  showTaskVolume: PropTypes.bool,
  fitContent: PropTypes.bool,
  showTaskNumber: PropTypes.bool,
  cardType: PropTypes.string,
};

TaskInfoWrapper.defaultProps = {
  children: null,
  showTaskVolume: false,
  fitContent: false,
  showTaskNumber: false,
  cardType: '',
  tasks: [],
  style: {},
};

const areEqual = (prevProps, nextProps) =>
  prevProps.showTaskVolume === nextProps.showTaskVolume &&
  prevProps.fitContent === nextProps.fitContent &&
  prevProps.showTaskNumber === nextProps.showTaskNumber &&
  prevProps.cardType === nextProps.cardType &&
  _.isEqual(prevProps.children, nextProps.children) &&
  _.isEqual(prevProps.tasks, nextProps.tasks);

export default React.memo(TaskInfoWrapper, areEqual);
